import {
  CnpjOrCpfInput,
  convertEventSelectToTarget,
  formatDataToLabelValue,
  FormFeedBack,
  MainButton,
  ModalDelete,
  OverlineTitle,
  PageWrapper,
  removeCnpjMask,
  removeCpfMask,
  removeZipCodeMask,
  Select,
  SelectRemote,
  SingleMarkerMap,
  STATUSPAGE,
  ZipCodeInput,
} from '@repo/ui';
import { CpfInput, TelPhone } from 'adasi_components';
import FeatherIcon from 'feather-icons-react';
import { useEffect } from 'react';
import Skeleton from 'react-loading-skeleton';
import {
  Button,
  Card,
  CardFooter,
  Col,
  Form,
  Input,
  Label,
  Row,
  Spinner,
} from 'reactstrap';

import { useCreateOrUpdateInternshipLocation } from '../hooks/use-create-or-update-internship-location';

export const CreateOrUpdateInternshipLocation = () => {
  // Hooks
  const {
    id,
    cpfSchema,
    validation,
    emailSchema,
    zipCodeSchema,
    requestsStatus,
    photoFileInputRef,
    photoFilePreviewUrl,
    areInputsAndBtnsDisabled,
    citiesForSelectOptionsData,
    countriesData,
    isConfirmDeleteImageModalOpen,
    selectedInternshipLocationData,
    navigate,
    requestSearchByCnpj,
    handlePhotoInputChange,
    setPhotoFilePreviewUrl,
    requestGetAllCountries,
    requestGetAddressViaZipCode,
    toggleConfirmDeleteImageModal,
    setCitiesForSelectOptionsData,
    verifyInternshipResponsibleCpf,
    requestGetInternshipLocationById,
    handleAddOrEditMarkerLocationBtnClick,
    verifyInternshipResponsibleEmail,
    resetInputsOnCountryChange,
    requestGetAllCitiesForSelectOptions,
    requestDeleteInternshipLocationImage,
    removeSelectedInternshipLocationData,
  } = useCreateOrUpdateInternshipLocation();

  const hasLocationMarker =
    !!selectedInternshipLocationData?.lat &&
    !!selectedInternshipLocationData?.lng;

  useEffect(() => {
    if (id) {
      requestGetInternshipLocationById({ id });
    }

    return () => {
      removeSelectedInternshipLocationData();
    };
  }, [id]);

  useEffect(() => {
    if (selectedInternshipLocationData) {
      setPhotoFilePreviewUrl(selectedInternshipLocationData.photo || '');
    }
  }, [selectedInternshipLocationData]);

  useEffect(() => {
    requestGetAllCountries();
  }, []);

  return (
    <PageWrapper title={`${id ? 'Editar' : 'Novo'} Local de Estágio`}>
      <div className="mb-4">
        <h4>{id ? 'Editar' : 'Novo'} Local de Estágio</h4>
      </div>

      <Card className="p-3">
        <Form
          autoComplete="off"
          onSubmit={(e) => {
            e.preventDefault();
            validation?.handleSubmit();
            return false;
          }}
        >
          <div className="my-2">
            <OverlineTitle title="Dados Cadastrais" />
          </div>
          <Row>
            <Col xl={id ? 7 : 12}>
              <Row>
                <Col sm={5} lg={4} className="mb-3">
                  <Label htmlFor="cnpjOrCpf" className="form-label">
                    CNPJ/CPF
                  </Label>
                  <CnpjOrCpfInput
                    id="cnpjOrCpf"
                    name="cnpjOrCpf"
                    onChange={(e) => {
                      validation.handleChange(e);
                      if (e.target.value && e.target.value.length === 18) {
                        requestSearchByCnpj({
                          cnpj: removeCnpjMask(e.target.value),
                        });
                      }
                    }}
                    onBlur={validation.handleBlur}
                    value={validation.values.cnpjOrCpf}
                    disabled={id ? true : areInputsAndBtnsDisabled}
                    invalid={
                      !!(
                        validation?.touched?.cnpjOrCpf &&
                        validation?.errors?.cnpjOrCpf
                      )
                    }
                  />
                  <FormFeedBack
                    error={validation?.errors?.cnpjOrCpf}
                    isInvalid={
                      !!(
                        validation?.touched?.cnpjOrCpf &&
                        validation?.errors?.cnpjOrCpf
                      )
                    }
                  />
                </Col>

                <Col sm={7} lg={8} className="mb-3">
                  <Label htmlFor="name" className="form-label">
                    Nome
                  </Label>
                  <Input
                    id="name"
                    type="text"
                    name="name"
                    onBlur={validation?.handleBlur}
                    value={validation?.values?.name}
                    onChange={validation?.handleChange}
                    disabled={areInputsAndBtnsDisabled}
                    invalid={
                      validation?.touched?.name && !!validation?.errors?.name
                    }
                  />
                  <FormFeedBack
                    error={validation?.errors?.name}
                    isInvalid={
                      validation?.touched?.name && !!validation?.errors?.name
                    }
                  />
                </Col>

                <Col sm={7} lg={8} className="mb-3">
                  <Label htmlFor="alternativeName" className="form-label">
                    Nome Alternativo
                  </Label>
                  <Input
                    id="alternativeName"
                    type="text"
                    name="alternativeName"
                    onBlur={validation?.handleBlur}
                    value={validation?.values?.alternativeName}
                    onChange={validation?.handleChange}
                    disabled={areInputsAndBtnsDisabled}
                    invalid={
                      validation?.touched?.alternativeName &&
                      !!validation?.errors?.alternativeName
                    }
                  />
                  <FormFeedBack
                    error={validation?.errors?.alternativeName}
                    isInvalid={
                      validation?.touched?.alternativeName &&
                      !!validation?.errors?.alternativeName
                    }
                  />
                  <small className="text-muted">
                    Caso preenchido, este nome será exibido no lugar do nome
                    oficial.
                  </small>
                </Col>

                <Col sm={7} xl={4} className="mb-3">
                  <Label>País</Label>
                  <Select
                    id="country"
                    name="country"
                    value={validation?.values?.country}
                    onChange={(e) => {
                      resetInputsOnCountryChange(e ? e.value : null);
                      validation?.handleChange(
                        convertEventSelectToTarget('country', e),
                      );
                    }}
                    onBlur={validation?.handleBlur}
                    options={formatDataToLabelValue(
                      countriesData as never,
                      'id',
                      'name',
                    )}
                    placeholder="Selecione o país"
                    isClearable
                    isDisabled={areInputsAndBtnsDisabled}
                    invalid={
                      !!(
                        validation?.touched?.country &&
                        validation?.errors?.country
                      )
                    }
                  />
                  <FormFeedBack
                    error={validation?.errors?.country as string}
                    isInvalid={
                      validation?.touched?.country &&
                      !!validation?.errors?.country
                    }
                  />
                </Col>

                <Col sm={5} xl={2} className="mb-3">
                  {validation.values.country?.value === 'BR' ||
                  !validation.values.country?.value ? (
                    <>
                      <Label htmlFor="zipCode">CEP</Label>
                      <ZipCodeInput
                        id="zipCode"
                        name="zipCode"
                        onChange={(e) => {
                          const { value } = e.target;

                          if (value && zipCodeSchema.isValidSync(value)) {
                            requestGetAddressViaZipCode({
                              zipCode: removeZipCodeMask(value),
                            });
                          }

                          validation?.handleChange(e);
                        }}
                        onBlur={validation?.handleBlur}
                        value={validation?.values?.zipCode}
                        disabled={
                          requestsStatus.getAddressViaZipCode ===
                            STATUSPAGE.LOADING || areInputsAndBtnsDisabled
                        }
                        invalid={
                          !!(
                            validation?.touched.zipCode &&
                            validation?.errors?.zipCode
                          )
                        }
                      />
                      <FormFeedBack
                        error={validation?.errors?.zipCode}
                        isInvalid={
                          validation?.touched?.zipCode &&
                          !!validation?.errors?.zipCode
                        }
                      />
                    </>
                  ) : (
                    <>
                      <Label htmlFor="nonBrazilianZipCode">Código postal</Label>
                      <Input
                        id="nonBrazilianZipCode"
                        type="text"
                        name="nonBrazilianZipCode"
                        onBlur={validation?.handleBlur}
                        value={validation?.values?.nonBrazilianZipCode}
                        onChange={validation?.handleChange}
                        disabled={areInputsAndBtnsDisabled}
                        invalid={
                          !!(
                            validation?.touched.nonBrazilianZipCode &&
                            validation?.errors?.nonBrazilianZipCode
                          )
                        }
                      />
                      <FormFeedBack
                        error={validation?.errors?.nonBrazilianZipCode}
                        isInvalid={
                          validation?.touched?.nonBrazilianZipCode &&
                          !!validation?.errors?.nonBrazilianZipCode
                        }
                      />
                    </>
                  )}
                </Col>

                <Col sm={6} className="mb-3">
                  <Label htmlFor="street" className="form-label">
                    Endereço
                  </Label>
                  <Input
                    type="text"
                    name="street"
                    onBlur={validation?.handleBlur}
                    value={validation?.values?.street}
                    onChange={(e) => {
                      validation?.handleChange(e);
                    }}
                    disabled={
                      requestsStatus.getAddressViaZipCode ===
                        STATUSPAGE.LOADING || areInputsAndBtnsDisabled
                    }
                    invalid={
                      !!(
                        validation?.touched.street && validation?.errors?.street
                      )
                    }
                  />
                  <FormFeedBack
                    error={validation?.errors?.street}
                    isInvalid={
                      validation?.touched?.street &&
                      !!validation?.errors?.street
                    }
                  />
                </Col>

                <Col sm={3} xl={2} className="mb-3">
                  <Label htmlFor="street" className="form-label">
                    Número
                  </Label>
                  <Input
                    type="text"
                    name="addressNumber"
                    onBlur={validation?.handleBlur}
                    value={validation?.values?.addressNumber}
                    disabled={
                      requestsStatus.getAddressViaZipCode ===
                        STATUSPAGE.LOADING || areInputsAndBtnsDisabled
                    }
                    onChange={validation?.handleChange}
                    invalid={
                      !!(
                        validation?.touched.addressNumber &&
                        validation?.errors?.addressNumber
                      )
                    }
                  />
                  <FormFeedBack
                    error={validation?.errors?.addressNumber}
                    isInvalid={
                      validation?.touched?.addressNumber &&
                      !!validation?.errors?.addressNumber
                    }
                  />
                </Col>

                <Col sm={3} xl={2} className="mb-3">
                  <Label htmlFor="addressComplement" className="form-label">
                    Complemento
                  </Label>
                  <Input
                    id="addressComplement"
                    type="text"
                    name="addressComplement"
                    onBlur={validation?.handleBlur}
                    value={validation?.values?.addressComplement}
                    onChange={validation?.handleChange}
                    disabled={areInputsAndBtnsDisabled}
                    invalid={
                      !!(
                        validation?.touched.addressComplement &&
                        validation?.errors?.addressComplement
                      )
                    }
                  />
                  <FormFeedBack
                    error={validation?.errors?.addressComplement}
                    isInvalid={
                      validation?.touched?.addressComplement &&
                      !!validation?.errors?.addressComplement
                    }
                  />
                </Col>

                <Col sm={6} xl={4} className="mb-3">
                  <Label htmlFor="district" className="form-label">
                    {!validation?.values?.country ||
                    validation?.values?.country?.value === 'BR'
                      ? 'Bairro'
                      : 'Bairro/Distrito'}
                  </Label>
                  <Input
                    type="text"
                    name="district"
                    onBlur={validation?.handleBlur}
                    value={validation?.values?.district}
                    onChange={(e) => {
                      validation?.handleChange(e);
                    }}
                    disabled={areInputsAndBtnsDisabled}
                    invalid={
                      !!(
                        validation?.touched?.district &&
                        validation?.errors?.district
                      )
                    }
                  />
                  <FormFeedBack
                    error={validation?.errors?.district}
                    isInvalid={
                      validation?.touched?.district &&
                      !!validation?.errors?.district
                    }
                  />
                </Col>

                <Col sm={6} xl={4} className="mb-3">
                  {!validation?.values?.country ||
                  validation?.values?.country?.value === 'BR' ? (
                    <>
                      <Label htmlFor="city" className="form-label">
                        Cidade
                      </Label>
                      <SelectRemote
                        name="city"
                        clearOptions={() => {
                          setCitiesForSelectOptionsData([]);
                        }}
                        functionSearch={(e) => {
                          if (e.length >= 3) {
                            requestGetAllCitiesForSelectOptions({ search: e });
                          }
                        }}
                        inputId={'city'}
                        onChange={(e) => {
                          validation?.handleChange(
                            convertEventSelectToTarget('city', e),
                          );
                        }}
                        onBlur={validation?.handleBlur}
                        options={formatDataToLabelValue(
                          citiesForSelectOptionsData as never,
                          'id',
                          'name',
                        )}
                        value={validation?.values?.city}
                        loading={
                          requestsStatus.getAllCitiesForSelectOptions ===
                            STATUSPAGE.LOADING ||
                          requestsStatus.getAddressViaZipCode ===
                            STATUSPAGE.LOADING
                        }
                      />
                      <FormFeedBack
                        error={validation?.errors?.city as string}
                        isInvalid={
                          validation?.touched?.city &&
                          !!validation?.errors?.city
                        }
                      />
                    </>
                  ) : (
                    <>
                      <Label htmlFor="nonBrazilianCity" className="form-label">
                        Cidade
                      </Label>
                      <Input
                        type="text"
                        name="nonBrazilianCity"
                        onBlur={validation?.handleBlur}
                        value={validation?.values?.nonBrazilianCity}
                        onChange={validation?.handleChange}
                        disabled={areInputsAndBtnsDisabled}
                        invalid={
                          !!(
                            validation?.touched?.nonBrazilianCity &&
                            validation?.errors?.nonBrazilianCity
                          )
                        }
                      />
                      <FormFeedBack
                        error={validation?.errors?.nonBrazilianCity}
                        isInvalid={
                          validation?.touched?.nonBrazilianCity &&
                          !!validation?.errors?.nonBrazilianCity
                        }
                      />
                    </>
                  )}
                </Col>

                {validation?.values?.country &&
                  validation?.values?.country.value !== 'BR' && (
                    <Col sm={6} xl={4} className="mb-3">
                      <Label htmlFor="nonBrazilianState" className="form-label">
                        Estado/Província/Região
                      </Label>
                      <Input
                        id="nonBrazilianState"
                        type="text"
                        name="nonBrazilianState"
                        onBlur={validation?.handleBlur}
                        value={validation?.values?.nonBrazilianState}
                        onChange={validation?.handleChange}
                        disabled={areInputsAndBtnsDisabled}
                        invalid={
                          !!(
                            validation?.touched.nonBrazilianState &&
                            validation?.errors?.nonBrazilianState
                          )
                        }
                      />
                      <FormFeedBack
                        error={validation?.errors?.nonBrazilianState}
                        isInvalid={
                          validation?.touched?.nonBrazilianState &&
                          !!validation?.errors?.nonBrazilianState
                        }
                      />
                    </Col>
                  )}

                <Col sm={6} xl={4} className="mb-3">
                  <Label htmlFor="ineCode" className="form-label">
                    Código INE
                  </Label>
                  <Input
                    id="ineCode"
                    type="number"
                    name="ineCode"
                    onBlur={validation?.handleBlur}
                    value={validation?.values?.ineCode}
                    onChange={(e) => {
                      validation?.handleChange(e);
                      if (e.target.value.length === 0) {
                        validation.setFieldValue('ineCode', undefined);
                      }
                    }}
                    disabled={areInputsAndBtnsDisabled}
                    invalid={
                      validation?.touched?.ineCode &&
                      !!validation?.errors?.ineCode
                    }
                  />
                  <FormFeedBack
                    error={validation?.errors?.ineCode}
                    isInvalid={
                      validation?.touched?.ineCode &&
                      !!validation?.errors?.ineCode
                    }
                  />
                </Col>

                <Col xl={12} className="mb-3">
                  <Label htmlFor="observation" className="form-label">
                    Observação
                  </Label>
                  <Input
                    rows={4}
                    id="observation"
                    type="textarea"
                    name="observation"
                    onBlur={validation?.handleBlur}
                    value={validation?.values?.observation}
                    onChange={validation?.handleChange}
                    disabled={areInputsAndBtnsDisabled}
                  />
                </Col>
                <div className="mb-1">
                  <Input
                    type="checkbox"
                    id="isRemote"
                    name="isRemote"
                    className="form-check-input"
                    onBlur={validation.handleBlur}
                    onChange={validation.handleChange}
                    checked={validation?.values?.isRemote}
                  />
                  <label className="form-check-label ms-2" htmlFor="isRemote">
                    Local Remoto
                  </label>
                </div>
              </Row>
            </Col>

            {id && (
              <Col xl={5}>
                <Row style={{ minHeight: '10rem' }}>
                  <Col xs={12} className="mb-3">
                    <Label htmlFor="photo" className="form-label">
                      Foto do Local
                    </Label>
                    <Input
                      id="photo"
                      innerRef={photoFileInputRef}
                      type="file"
                      name="photo"
                      accept=".png, .jpg, .jpeg"
                      onChange={handlePhotoInputChange}
                      disabled={
                        areInputsAndBtnsDisabled ||
                        requestsStatus.updateInternshipLocationImage ===
                          STATUSPAGE.LOADING ||
                        requestsStatus.deleteInternshipLocationImage ===
                          STATUSPAGE.LOADING
                      }
                    />
                  </Col>
                  <Col xs={12}>
                    <div className="d-flex justify-content-center align-items-center">
                      {requestsStatus.updateInternshipLocationImage ===
                        STATUSPAGE.LOADING ||
                      requestsStatus.deleteInternshipLocationImage ===
                        STATUSPAGE.LOADING ? (
                        <Spinner
                          style={{
                            height: '48px',
                            width: '48px',
                          }}
                        >
                          {['Carregando...']}
                        </Spinner>
                      ) : (
                        photoFilePreviewUrl && (
                          <div className="position-relative">
                            <img
                              src={photoFilePreviewUrl}
                              alt="Foto do Local de Estágio"
                              className="img-fluid my-2 object-fit-contain rounded"
                              style={{ maxHeight: '500px' }}
                            />
                            <Button
                              onClick={() => {
                                toggleConfirmDeleteImageModal();
                              }}
                              color="danger"
                              type="button"
                              className="rounded-circle p-2 position-absolute"
                              style={{ top: 0, right: -4, zIndex: 2 }}
                            >
                              <FeatherIcon icon="trash-2" size={18} />
                            </Button>
                          </div>
                        )
                      )}
                    </div>
                  </Col>
                </Row>
              </Col>
            )}
          </Row>

          <div className="my-2">
            <OverlineTitle title="Dados do Responsável pelo Estágio" />
          </div>
          <Row>
            <Col xs={12}>
              <Row>
                <Col sm={5} lg={3} xl={2} className="mb-3">
                  <Label
                    htmlFor="internshipResponsible.cpf"
                    className="form-label"
                  >
                    CPF
                  </Label>
                  <CpfInput
                    name="internshipResponsible.cpf"
                    inputId="internshipResponsible.cpf"
                    className="form-control"
                    value={validation.values.internshipResponsible.cpf}
                    handleChange={(e) => {
                      const { value } = e.target;

                      if (
                        removeCpfMask(value).length === 11 &&
                        cpfSchema.isValidSync(value)
                      ) {
                        verifyInternshipResponsibleCpf({
                          cpf: removeCpfMask(value),
                        });
                      }

                      validation.handleChange(e);
                    }}
                    onBlur={validation.handleBlur}
                    disabled={
                      areInputsAndBtnsDisabled ||
                      requestsStatus.verifyInternshipResponsibleCpf ===
                        STATUSPAGE.LOADING
                    }
                    invalid={
                      !!(
                        validation?.touched?.internshipResponsible?.cpf &&
                        validation?.errors?.internshipResponsible?.cpf
                      )
                    }
                  />
                  <FormFeedBack
                    error={
                      validation?.errors?.internshipResponsible?.cpf as string
                    }
                    isInvalid={
                      validation?.touched?.internshipResponsible?.cpf &&
                      !!validation?.errors?.internshipResponsible?.cpf
                    }
                  />
                </Col>

                <Col sm={7} lg={5} xl={6} className="mb-3">
                  <Label
                    htmlFor="internshipResponsible.name"
                    className="form-label"
                  >
                    Nome
                  </Label>
                  <Input
                    id="internshipResponsible.name"
                    name="internshipResponsible.name"
                    type="text"
                    onBlur={validation?.handleBlur}
                    value={validation?.values?.internshipResponsible.name}
                    onChange={validation?.handleChange}
                    disabled={
                      areInputsAndBtnsDisabled ||
                      requestsStatus.verifyInternshipResponsibleCpf ===
                        STATUSPAGE.LOADING
                    }
                    invalid={
                      validation?.touched?.internshipResponsible?.name &&
                      !!validation?.errors?.internshipResponsible?.name
                    }
                  />
                  <FormFeedBack
                    error={
                      validation?.errors?.internshipResponsible?.name as string
                    }
                    isInvalid={
                      validation?.touched?.internshipResponsible?.name &&
                      !!validation?.errors?.internshipResponsible?.name
                    }
                  />
                </Col>

                <Col sm={7} lg={4} className="mb-3">
                  <Label
                    htmlFor="internshipResponsible.role"
                    className="form-label"
                  >
                    Cargo
                  </Label>
                  <Input
                    id="internshipResponsible.role"
                    name="internshipResponsible.role"
                    type="text"
                    onBlur={validation?.handleBlur}
                    value={validation?.values?.internshipResponsible?.role}
                    onChange={validation?.handleChange}
                    disabled={
                      areInputsAndBtnsDisabled ||
                      requestsStatus.verifyInternshipResponsibleCpf ===
                        STATUSPAGE.LOADING
                    }
                    invalid={
                      validation?.touched?.internshipResponsible?.role &&
                      !!validation?.errors?.internshipResponsible?.role
                    }
                  />
                  <FormFeedBack
                    error={
                      validation?.errors?.internshipResponsible?.role as string
                    }
                    isInvalid={
                      validation?.touched?.internshipResponsible?.role &&
                      !!validation?.errors?.internshipResponsible?.role
                    }
                  />
                </Col>

                <Col sm={5} lg={3} xl={2} className="mb-3">
                  <Label
                    htmlFor="internshipResponsible.phone"
                    className="form-label"
                  >
                    Telefone
                  </Label>
                  <TelPhone
                    id="internshipResponsible.phone"
                    name="internshipResponsible.phone"
                    value={`${validation.values.internshipResponsible.phone}`}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      const inputValue = {
                        target: {
                          name: 'internshipResponsible.phone',
                          value: e,
                        },
                      };
                      validation.handleChange(inputValue);
                    }}
                    onBlur={validation.handleBlur}
                  />
                  <FormFeedBack
                    error={
                      validation?.errors?.internshipResponsible?.phone as string
                    }
                    isInvalid={
                      validation?.touched?.internshipResponsible?.phone &&
                      !!validation?.errors?.internshipResponsible?.phone
                    }
                  />
                </Col>

                <Col sm={6} lg={5} xl={4} className="mb-3">
                  <Label
                    htmlFor="internshipResponsible.email"
                    className="form-label"
                  >
                    E-mail
                  </Label>
                  <Input
                    id="internshipResponsible.email"
                    name="internshipResponsible.email"
                    type="email"
                    onBlur={(e) => {
                      const { value } = e.target;

                      if (value.length > 1 && emailSchema.isValidSync(value)) {
                        verifyInternshipResponsibleEmail({ email: value });
                      }
                      validation?.handleBlur(e);
                    }}
                    value={validation?.values?.internshipResponsible.email}
                    onChange={validation?.handleChange}
                    disabled={
                      areInputsAndBtnsDisabled ||
                      requestsStatus.verifyInternshipResponsibleCpf ===
                        STATUSPAGE.LOADING ||
                      requestsStatus.verifyInternshipResponsibleEmail ===
                        STATUSPAGE.LOADING
                    }
                    invalid={
                      validation?.touched?.internshipResponsible?.email &&
                      !!validation?.errors?.internshipResponsible?.email
                    }
                  />
                  <FormFeedBack
                    error={
                      validation?.errors?.internshipResponsible?.email as string
                    }
                    isInvalid={
                      validation?.touched?.internshipResponsible?.email &&
                      !!validation?.errors?.internshipResponsible?.email
                    }
                  />
                </Col>
              </Row>
            </Col>
          </Row>

          {id && (
            <>
              <div className="my-2">
                <OverlineTitle title="Georreferenciamento" />
              </div>
              <Row>
                <Col xs={12} className="mb-3">
                  <Row>
                    <Col className="d-flex flex-column justify-content-center align-items-start">
                      <Label>Localização do marcador no mapa</Label>
                      {requestsStatus.getInternshipLocationById ===
                      STATUSPAGE.LOADING ? (
                        <Skeleton
                          style={{ height: 470 }}
                          baseColor="#e8e8e8c5"
                          highlightColor="#bababa96"
                        />
                      ) : (
                        <SingleMarkerMap
                          lat={selectedInternshipLocationData?.lat}
                          lng={selectedInternshipLocationData?.lng}
                        />
                      )}
                    </Col>
                  </Row>
                </Col>
              </Row>
            </>
          )}

          <CardFooter className="pb-0 px-0 d-flex justify-content-end gap-4">
            <Button
              onClick={() => {
                navigate('/cadastros/locais-de-estagio');
              }}
              color="link"
              className="text-decoration-underline p-1"
            >
              Voltar
            </Button>
            {id && (
              <Button
                onClick={() =>
                  handleAddOrEditMarkerLocationBtnClick(hasLocationMarker)
                }
                disabled={
                  areInputsAndBtnsDisabled ||
                  requestsStatus.verifyInternshipResponsibleCpf ===
                    STATUSPAGE.LOADING ||
                  requestsStatus.verifyInternshipResponsibleEmail ===
                    STATUSPAGE.LOADING ||
                  requestsStatus.getAllCitiesForSelectOptions ===
                    STATUSPAGE.LOADING ||
                  requestsStatus.getAddressViaZipCode === STATUSPAGE.LOADING ||
                  requestsStatus.getCoordinatesFromAddress ===
                    STATUSPAGE.LOADING
                }
                className="text-decoration-underline p-1"
                color="link"
              >
                {hasLocationMarker ? 'Editar' : 'Adicionar'} marcador
              </Button>
            )}
            <MainButton
              type="submit"
              isDisabled={
                areInputsAndBtnsDisabled ||
                requestsStatus.verifyInternshipResponsibleCpf ===
                  STATUSPAGE.LOADING ||
                requestsStatus.verifyInternshipResponsibleEmail ===
                  STATUSPAGE.LOADING ||
                requestsStatus.getAllCitiesForSelectOptions ===
                  STATUSPAGE.LOADING ||
                requestsStatus.getAddressViaZipCode === STATUSPAGE.LOADING
              }
              isLoading={
                requestsStatus.createOrUpdate === STATUSPAGE.LOADING ||
                requestsStatus.getCoordinatesFromAddress === STATUSPAGE.LOADING
              }
            >
              Salvar
            </MainButton>
          </CardFooter>
        </Form>
      </Card>

      <ModalDelete
        isOpen={isConfirmDeleteImageModalOpen}
        toggle={toggleConfirmDeleteImageModal}
        title="Excluir foto"
        subtitle="Deseja realmente excluir a foto deste Local de Estágio?"
        handleDelete={() =>
          requestDeleteInternshipLocationImage({
            id,
          })
        }
        loading={
          requestsStatus.deleteInternshipLocationImage === STATUSPAGE.LOADING
        }
      />
    </PageWrapper>
  );
};
